/**
 * Persistent banner that warns every signed-in user when there's an
 * upcoming office closure within the next 14 days. Dismissible per
 * closure ID — once dismissed it stays dismissed until that closure
 * passes (or a new one is added).
 *
 * Mounted in App.tsx between the header and the main view.
 */
import { useState, useMemo } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { formatDayLabel } from "../utils/dates";

const DISMISS_KEY = "icons_closure_banner_dismissed_v1";
const LOOKAHEAD_DAYS = 14;

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveDismissed(set: Set<string>): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore quota / private mode */
  }
}

export function ClosureBanner(): JSX.Element | null {
  const s = state.value;
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);

  // Compute which closures fall in the next LOOKAHEAD_DAYS window AND
  // haven't been dismissed by this user. We re-derive on each render
  // (cheap — closures map is tiny).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const horizon = new Date(today);
  horizon.setDate(today.getDate() + LOOKAHEAD_DAYS);
  const horizonKey = horizon.toISOString().slice(0, 10);
  const todayKey = today.toISOString().slice(0, 10);

  // `s.closures` is supposed to always be present (defaulted to {} in
  // store init + loadCachedState), but a stale cache from before that
  // safety net could still hand us undefined. Coerce defensively inside
  // the memo so a missing field can never crash the render and the
  // exhaustive-deps lint stays happy.
  const upcoming = useMemo(() => {
    const closures = s.closures ?? {};
    return Object.entries(closures)
      .filter(([day]) => day >= todayKey && day <= horizonKey)
      .filter(([day]) => !dismissed.has(day))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, label]) => ({ day, label }));
  }, [s.closures, dismissed, todayKey, horizonKey]);

  if (upcoming.length === 0) return null;

  const dismissAll = (): void => {
    const next = new Set(dismissed);
    upcoming.forEach((c) => next.add(c.day));
    setDismissed(next);
    saveDismissed(next);
  };

  const dismissOne = (day: string): void => {
    const next = new Set(dismissed);
    next.add(day);
    setDismissed(next);
    saveDismissed(next);
  };

  return (
    <div class="closure-banner" role="status" aria-live="polite">
      <div class="closure-banner-icon" aria-hidden="true">📅</div>
      <div class="closure-banner-body">
        <div class="closure-banner-title">
          {upcoming.length === 1 ? "Office closure ahead" : `${upcoming.length} office closures ahead`}
        </div>
        <ul class="closure-banner-list">
          {upcoming.map((c) => (
            <li key={c.day}>
              <strong>{formatDayLabel(c.day)}</strong>
              {c.label && <span class="closure-banner-label"> — {c.label}</span>}
              <button
                type="button"
                class="closure-banner-dismiss-one"
                onClick={() => dismissOne(c.day)}
                aria-label={`Dismiss ${formatDayLabel(c.day)} reminder`}
                title="Dismiss this reminder"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </div>
      <button
        type="button"
        class="closure-banner-dismiss"
        onClick={dismissAll}
        aria-label="Dismiss all closure reminders"
      >
        Dismiss all
      </button>
    </div>
  );
}
